import { DocumentType } from "generated-graphql/graphql";

/**
 * Whether a review request from the documents catalog should open the manifest
 * review screen rather than the generic metadata form.
 *
 * The catalog's Edit opens one dialog for every document type, which on a
 * manifest means editing the file's title and tags instead of the data read off
 * the form. For most manifests that is merely the wrong screen — Shipment
 * Documents has a row that opens the right one. For a manifest whose generator
 * EPA ID matched no facility it is the whole story: the catalog is the only
 * place that lists it, so the wrong dialog made it unreviewable.
 *
 * Mixed selections keep the generic form. A batch spanning types has no single
 * right screen, and silently reviewing only the manifests in it would be a
 * worse surprise than the form the user asked for.
 */
export function shouldOpenManifestReview({
  documentTypes,
  wasteModuleEnabled,
}: {
  /** The type of each document in the review request, in any order. */
  documentTypes: (DocumentType | null | undefined)[];
  /** The tenant's stored waste-module value — manifest data only exists where the backend ingests it. */
  wasteModuleEnabled: boolean;
}): boolean {
  if (!wasteModuleEnabled) return false;
  if (documentTypes.length === 0) return false;
  return documentTypes.every((documentType) => documentType === DocumentType.RcraManifest);
}

/**
 * Whether the catalog's Edit opens the file-metadata form for a document.
 *
 * With document-ai, Edit opens a review instead, chosen by
 * `catalogManifestReviewIds`. Without it the metadata form is the default, but
 * not for a manifest: the waste pipeline extracts a manifest's data whether or
 * not document-ai is on, so its Edit is still that data's review.
 */
export function opensMetadataForm({
  hasDocumentAI,
  wasteModuleEnabled,
  documentType,
}: {
  hasDocumentAI: boolean;
  wasteModuleEnabled: boolean;
  documentType: DocumentType | null | undefined;
}): boolean {
  if (hasDocumentAI) return false;
  return !shouldOpenManifestReview({ documentTypes: [documentType], wasteModuleEnabled });
}

/**
 * The documents the catalog opens the manifest review for, or none.
 *
 * With document-ai, the review request (bulk selection or the URL's document)
 * when it is all manifests. Without it, the URL's document when it is a
 * manifest — the counterpart of `opensMetadataForm` declining it.
 */
export function catalogManifestReviewIds({
  hasDocumentAI,
  wasteModuleEnabled,
  documentsToReview,
  documentTypes,
  documentId,
  document,
}: {
  hasDocumentAI: boolean;
  wasteModuleEnabled: boolean;
  /** The document-ai review request; always empty without document-ai. */
  documentsToReview: string[];
  /** The type of each of `documentsToReview`, in the same order. */
  documentTypes: (DocumentType | null | undefined)[];
  /** The document the URL names. */
  documentId: string | null | undefined;
  /** That document, once loaded. */
  document: { id: string; documentType?: DocumentType | null } | null | undefined;
}): string[] {
  if (hasDocumentAI) {
    return shouldOpenManifestReview({ documentTypes, wasteModuleEnabled }) ? documentsToReview : [];
  }
  if (!document || document.id !== documentId) return [];
  const isManifest = !opensMetadataForm({
    hasDocumentAI,
    wasteModuleEnabled,
    documentType: document.documentType,
  });
  return isManifest ? [document.id] : [];
}
